import {BASE_PATH, PAY_MAIN_URI_BASE} from '$share/config'
import {isLocalProfile} from '$share/utils/env'

import type {IncomingMessage} from 'http'
import type {GetServerSidePropsContext} from 'next'

export function isSSR() {
    return typeof window === 'undefined'
}

export const noop = () => {
    // nothing to do
}

/** 연속 공백을 특수문자로 치환 */
export function displaySpace(str?: string | null) {
    return (str || '').replace(/\s/g, '\u00A0')
}

export const numberFormat = (value: number) => {
    // .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
    let formattedNumber = '0'

    if (value) {
        formattedNumber = `${value}`
    }

    const reg = /(^[+-]?\d+)(\d{3})/

    while (reg.test(formattedNumber)) {
        formattedNumber = formattedNumber.replace(reg, '$1,$2')
    }

    return formattedNumber
}

export const getIsDomestic = (countryCode: string | null) => countryCode === 'KR'

export const getIsKoreaCurrency = (currencyCode: string) => currencyCode === 'KRW'

export const getCurrencyCodeUnit = (currencyCode: string | null | undefined, option?: {noUnitSpace?: boolean}) => {
    if (!currencyCode || getIsKoreaCurrency(currencyCode)) {
        return '원'
    }

    if (currencyCode === 'P') {
        return 'P'
    }

    return `${option?.noUnitSpace ? '' : ' '}${currencyCode}`
}

export const formatProfit = (amount: number) => {
    return `${amount > 0 ? '+' : ''}${numberFormat(amount)}%`
}

export const getDirectionClass = (num: number): 'ascend' | 'descend' | '' => {
    if (num > 0) {
        return 'ascend'
    }

    if (num < 0) {
        return 'descend'
    }

    return ''
}

export const getEvalProfit = (standardVal: number, nextVal: number, {digit = 2}: {digit?: number} = {}) => {
    const subVal = nextVal - standardVal
    const directionClass = getDirectionClass(subVal)

    if (standardVal === 0) {
        return {
            percent: subVal === 0 ? 0 : subVal > 0 ? 100 : -100,
            subVal,
            directionClass,
        }
    }

    return {
        percent: Number(((subVal / standardVal) * 100).toFixed(digit)),
        subVal,
        directionClass,
    }
}

export const formatPercentage = (percentage: number) => {
    let formattedNumber = numberFormat(Math.abs(percentage))

    if (Number.isInteger(percentage)) {
        formattedNumber += '.0'
    }

    return formattedNumber + '%'
}

export function getIsLoginUserInServer(request: IncomingMessage) {
    if (isLocalProfile && typeof request.headers.nid_login_stat !== 'string' && request.headers.cookie) {
        return request.headers.cookie.split(';').some((value) => {
            const trimmedValue = value.trim()

            return trimmedValue.startsWith('NID_SES=') || trimmedValue.startsWith('NID_AUT=')
        })
    }

    return typeof request.headers.nid_login_stat === 'string' && request.headers.nid_login_stat.startsWith('Success')
}

interface GetOriginOptions {
    withBasePath?: boolean
}
export function getOrigin(req?: IncomingMessage, options?: GetOriginOptions) {
    let protocol = 'https'
    let host = req ? req.headers['x-forwarded-host'] || req.headers.host : window.location.host
    const basePath = BASE_PATH || ''

    if (isLocalProfile) {
        // PFMS는 ssl 처리가 되어있지 않아서, http로 고정
        protocol = 'http'
    } else {
        host = (host || '').toString().split(':')[0]
    }

    const origin = protocol + '://' + host

    return origin + (options?.withBasePath ? basePath : '')
}

export function getCurrentUrl(context?: GetServerSidePropsContext): string {
    if (context) {
        const origin = getOrigin(context.req)
        return new URL(`${BASE_PATH}${context.resolvedUrl}`, origin).href
    }
    return isSSR() ? PAY_MAIN_URI_BASE : window.location.href
}

export function debounce<Args extends unknown[]>(callback: (...args: Args) => unknown, timeout = 700) {
    let timer: NodeJS.Timeout

    return (...args: Args) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            callback(...args)
        }, timeout)
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CompareCondition<T> = [keyof T | ((item: T) => any), 'asc' | 'desc']
export function compareSort<T>(...conditions: CompareCondition<T>[]) {
    return (a: T, b: T) => {
        for (const [keyOrFunction, order] of conditions) {
            const aValue = typeof keyOrFunction === 'function' ? keyOrFunction(a) : a[keyOrFunction]
            const bValue = typeof keyOrFunction === 'function' ? keyOrFunction(b) : b[keyOrFunction]
            if (aValue !== bValue) {
                if (order === 'asc') {
                    if (aValue < bValue) {
                        return -1
                    }

                    if (aValue > bValue) {
                        return 1
                    }
                } else {
                    if (aValue < bValue) {
                        return 1
                    }

                    if (aValue > bValue) {
                        return -1
                    }
                }
            }
        }
        return 0
    }
}

export function getFinanceFeOrigin(origin: string) {
    if (origin.includes('local')) {
        return origin.replace('local', 'new-qa')
    }

    if (origin.includes('alpha')) {
        return origin.replace('alpha', 'qa')
    }

    if (origin.includes('test')) {
        return origin.replace('test', 'qa')
    }

    return origin.replace(/[0-9]/, '')
}

export function goToPayAppMyAsset() {
    if (typeof window.payInAppBrowser?.showServiceMenu === 'function') {
        window.payInAppBrowser?.showServiceMenu(JSON.stringify({menuName: 'myasset'}))
    }
}

export function getThousandTruncAmount(amount: number) {
    return Math.trunc(amount / 1000) / 10
}

export function getAmountToFloatFormatString(amt: number) {
    const [integer, decimal] = (amt / 10000).toString().split('.')
    const decimalPart = decimal ? `${decimal[0]}` : '0'
    return `${integer}.${decimalPart}`
}

/**
 * 입력한 숫자의 자릿수를 0으로 바꿈
 * amount:12345 , digits:2 => 12300
 * amount:12345 , digits:4 => 10000
 */
export function 자릿수절삭(amount: number, digits: number) {
    const 자릿수 = Math.pow(10, digits)
    return Math.trunc(amount / 자릿수) * 자릿수
}

export const isMatchingRoute = (route: string, url: string) => {
    let resolvedUrl = url?.split('?')[0] || '/'
    resolvedUrl = BASE_PATH && resolvedUrl.startsWith(BASE_PATH) ? resolvedUrl.slice(BASE_PATH.length) : resolvedUrl

    const routePattern = route.replace(/\[([^\]]+)\]/g, '([^/]+)').replace(/\//g, '\\/')

    const regex = new RegExp(`^${routePattern}$`)
    return regex.test(resolvedUrl)
}
